import Txt from 'components/atoms/Txt/Txt';
import { AllowedStorageKey } from 'constants/persistentStorageRegistry';
import useStorage from 'hooks/useStorage';
import React from 'react';

const TEN_MINUTES_MS = 10 * 60 * 1000;

export function useCountdownTimer(
  countdownCheckKey: AllowedStorageKey = 'countdownStart',
  duration: number = TEN_MINUTES_MS,
) {
  const [startTimestamp, setStartTimestamp] = useStorage<number | null>(countdownCheckKey, null, {
    session: true,
  });

  // Calculate time left
  const [timeLeft, setTimeLeft] = React.useState(
    startTimestamp ? Math.max(0, duration - (Date.now() - startTimestamp)) : duration,
  );

  React.useEffect(() => {
    if (!startTimestamp) return;
    const interval = setInterval(() => {
      const newTimeLeft = Math.max(0, duration - (Date.now() - startTimestamp));
      setTimeLeft(newTimeLeft);
      if (newTimeLeft === 0) {
        setStartTimestamp(null);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [startTimestamp, duration, countdownCheckKey, setStartTimestamp]);

  const resetTimer = React.useCallback(() => setStartTimestamp(Date.now()), [setStartTimestamp]);

  const clearCountdown = React.useCallback(() => {
    setStartTimestamp(null);
  }, [setStartTimestamp]);

  const formatTime = React.useCallback((totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60000);
    const seconds = Math.floor((totalSeconds % 60000) / 1000);

    return (
      <Txt>
        {{ minutes }}m {{ seconds }}s
      </Txt>
    );
  }, []);

  const startCountdown = React.useCallback(() => {
    if (!startTimestamp) {
      setStartTimestamp(Date.now());
    }
  }, [setStartTimestamp, startTimestamp]);

  return {
    timeLeft,
    formatTime,
    clearCountdown,
    resetTimer,
    isReady: !!startTimestamp,
    startCountdown,
  };
}
